import React, { lazy, Suspense, useEffect, useRef } from "react";
import "./App.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { UserProvider, useUser } from "./Context/UserContext/UserContext";
import { FeedbackProvider } from "./Context/FeedbackContext/FeedbackContext";
import { ThemeProvider } from "./Context/ThemeContext/ThemeContext";
import {
  About,
  AnswersForm,
  CorrectionSuccess,
  CreateLeague,
  Dashboard,
  Landing,
  LeagueDashboard,
  Profile,
  LeagueList,
  LeagueSuccess,
  LoginForm,
  NewLeague,
  QuinipoloSuccess,
  Privacy,
  ResetPassword,
  SignUpForm,
  EmailConfirmation,
  SurveyForm,
  Admin,
  JoinLeague,
  Terms,
  DeleteAccount,
} from "./Routes";
import OAuthCallbackHandler from "./Components/OAuthCallbackHandler/OAuthCallbackHandler";
import ProtectedRoute from "./Components/ProtectedRoute/ProtectedRoute";
import Layout from "./Components/Layout/Layout";
import { config } from "./utils/config";
import { trackPageView } from "./utils/analytics";

const GraphicsGeneratorPage = lazy(() =>
  import("./Routes/GraphicsGenerator/GraphicsGeneratorPage").then((m) => ({
    default: m.default,
  })),
);

const GraphicsTeamsPage = lazy(() =>
  import("./Routes/GraphicsTeams/GraphicsTeamsPage").then((m) => ({
    default: m.default,
  })),
);

const TeamsCuratorPage = lazy(() =>
  import("./Routes/TeamsCurator/TeamsCuratorPage").then((m) => ({
    default: m.default,
  })),
);

const LogoMapperPage = lazy(() =>
  import("./Routes/LogoMapper/LogoMapperPage").then((m) => ({
    default: m.default,
  })),
);

// Custom hook to handle URL cleanup
function useUrlCleanup() {
  useEffect(() => {
    // Clean up trailing hash fragments (like /dashboard#)
    if (window.location.hash === "#") {
      // Remove the empty hash without triggering a page reload
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  }, []);
}

function App() {
  const { userData } = useUser();
  const isAuthenticated = userData.isAuthenticated;
  const authInitialized = userData.authInitialized;
  const shouldShowAuthed = Boolean(authInitialized && isAuthenticated);

  // Use the URL cleanup hook
  useUrlCleanup();

  // Track page views on route changes (must be inside Router)
  function RouteChangeTracker() {
    const location = useLocation();
    const lastPathRef = useRef<string | null>(null);
    useEffect(() => {
      const path = location.pathname + location.search;
      if (lastPathRef.current === path) return;
      lastPathRef.current = path;
      console.debug("[Analytics] route change detected", { path });
      trackPageView(path);
    }, [location]);
    return null;
  }

  return (
    <BrowserRouter>
      <RouteChangeTracker />
      <FeedbackProvider>
        <UserProvider>
          <ThemeProvider>
            <OAuthCallbackHandler>
              <Routes>
                <Route path="/sign-in" element={<LoginForm />} />
                <Route path="/signup" element={<SignUpForm />} />
                <Route
                  path="/email-confirmation"
                  element={<EmailConfirmation />}
                />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/join-league/:shareToken"
                  element={<JoinLeague />}
                />

                {/* Routes with layout (header+footer) accessible to all */}
                <Route path="/" element={<Layout />}>
                  {/* Public pages */}
                  <Route path="/about" element={<About />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />

                  {/* Authenticated pages */}
                  {shouldShowAuthed ? (
                    <>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="crear-quinipolo" element={<SurveyForm />} />
                      <Route
                        path="quinipolo-success"
                        element={<QuinipoloSuccess />}
                      />
                      <Route
                        path="correction-success"
                        element={<CorrectionSuccess />}
                      />
                      <Route path="quinipolo" element={<AnswersForm />}>
                        <Route path="correct" element={<AnswersForm />} />
                      </Route>
                      <Route
                        path="league-dashboard"
                        element={<LeagueDashboard />}
                      />
                      <Route path="profile" element={<Profile />} />
                      <Route path="join-league" element={<LeagueList />} />
                      <Route path="crear-liga" element={<NewLeague />} />
                      <Route path="create-league" element={<CreateLeague />} />
                      <Route
                        path="league-success"
                        element={<LeagueSuccess />}
                      />
                      <Route
                        path="admin"
                        element={
                          <ProtectedRoute requireAdmin={true} fallbackPath="/">
                            <Admin />
                          </ProtectedRoute>
                        }
                      />
                    </>
                  ) : (
                    <Route path="/" element={<Landing />} />
                  )}

                  {config.isDevelopment && (
                    <>
                      <Route
                        path="graphics"
                        element={
                          <ProtectedRoute fallbackPath="/">
                            <Suspense fallback={null}>
                              <GraphicsGeneratorPage />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="graphics/teams"
                        element={
                          <ProtectedRoute fallbackPath="/">
                            <Suspense fallback={null}>
                              <GraphicsTeamsPage />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="teams-curator"
                        element={
                          <ProtectedRoute fallbackPath="/">
                            <Suspense fallback={null}>
                              <TeamsCuratorPage />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="logo-mapper"
                        element={
                          <ProtectedRoute fallbackPath="/">
                            <Suspense fallback={null}>
                              <LogoMapperPage />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />
                    </>
                  )}
                  <Route path="*" element={<Navigate to="/" />} />
                </Route>

                {/* Keep a final catch-all to land on home within layout */}
              </Routes>
            </OAuthCallbackHandler>
          </ThemeProvider>
        </UserProvider>
      </FeedbackProvider>
    </BrowserRouter>
  );
}

export default App;
